import { useCallback } from "react";
import { useLocalStorage } from "react-use";
import type { PersistedSort } from "~/hooks/useSort";
import type { AgentListView } from "~/services/agents";
import type { ProjectListView } from "~/services/projects";

export interface McpToolPrefsEntry {
  selected: string[];
  available: string[];
}

export interface ConnectorPrefsEntry {
  /** Connector names the user has explicitly enabled for this context.
   *  Connectors default off, so anything absent here stays disabled. */
  selected: string[];
}

interface UserPreferences {
  disableStopEnvPrompt?: boolean;
  previousApp?: string;
  collapseWorkbenchNav?: boolean;
  collapseSessionContextSidebar?: boolean;
  mcpToolPreferences?: Record<string, McpToolPrefsEntry>;
  connectorPreferences?: Record<string, ConnectorPrefsEntry>;
  listSortPreferences?: Record<string, PersistedSort>;
  favoriteItems?: Record<string, string[]>;
  optedIntoManifoldNextGen?: boolean;
  /** Which agents-list slice to show (`usable` default, `editable`, or admin
   *  `all`); clamped back to `usable` if the caller's permissions don't allow it. */
  agentListView?: AgentListView;
  /** Which projects-list slice to show (`usable` default, `owned`, or admin
   *  `all`); clamped back to `usable` if the caller's permissions don't allow it. */
  projectListView?: ProjectListView;
  /** When true, the tool-call disclosure stays collapsed while a turn runs.
   *  Set when the user closes (or cleared when they open) the disclosure
   *  mid-run; absent means disclosures default open. */
  collapseToolDisclosureDuringRun?: boolean;
  /** Chart theme pinned from a figure's modebar. Absent means figures follow
   *  the app theme; see `useFigureTheme`. */
  figureTheme?: "light" | "dark";
}

const KEY = "sc-user-preferences";

// The freshest persisted blob, read straight from storage. `useLocalStorage`
// caches a per-instance snapshot taken at mount and never syncs same-tab
// writes, so this is the only way to see what a sibling instance just wrote.
export const readStoredPreferences = (): UserPreferences => {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? "{}");
  } catch {
    return {};
  }
};

type PreferencesUpdate = UserPreferences | ((current: UserPreferences) => UserPreferences);

/**
 * Manages temporary user preferences in client side storage
 */
export const useUserPreferences = (): [
  preferences: UserPreferences,
  updatePreferences: (update: PreferencesUpdate) => void,
] => {
  const [preferences, setPreferences] = useLocalStorage<UserPreferences>(KEY);

  // Both the merge base and any function update resolve against a fresh read,
  // not this instance's snapshot: several useUserPreferences() consumers can be
  // mounted at once (e.g. useSort and useFavorites), and react-use never syncs
  // same-tab writes. Pass a function to derive nested record fields (e.g.
  // favoriteItems[key]) from current storage, so a stale snapshot can't drop a
  // sibling's entry under the same field.
  // Stable (react-use's setter is) so callers can pass it on to stable callbacks.
  const updatePreferences = useCallback(
    (update: PreferencesUpdate) => {
      const current = readStoredPreferences();
      setPreferences({ ...current, ...(typeof update === "function" ? update(current) : update) });
    },
    [setPreferences]
  );

  return [preferences ?? {}, updatePreferences];
};
